import { useEffect, useMemo } from "react";
import { Link, useParams } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowLeft, Lightbulb, Sparkles, Wallet, Users, Calendar } from "lucide-react";

import { maybeShowRecapInterstitial } from "@/lib/ads";
import {
  computeDirectDebts,
  formatDateRange,
  formatDestinations,
  formatMoney,
  getTripStatus,
  type Activity,
  type Trip,
} from "@travelix/core";

import { useTrip, useTripActivities } from "@/hooks/use-trip";
import { Skeleton } from "@/components/ui/skeleton";
import { Button } from "@/components/ui/button";
import { isLikelyBrokenCoverUrl } from "@/lib/cover-url";
import { celebrate } from "@/lib/celebrate";

/**
 * Trip Recap — the end-of-trip "here's what happened" page.
 * Editorial layout; no live actions, just a beautifully computed summary.
 */
export default function TripRecapRoute() {
  const { id } = useParams<{ id: string }>();
  const { data: trip, isLoading: tripLoading } = useTrip(id);
  const { data: activities, isLoading: actsLoading } = useTripActivities(id);

  if (tripLoading || actsLoading || !trip) return <RecapSkeleton />;

  return <Recap trip={trip} activities={activities ?? []} />;
}

// ─── Recap ──────────────────────────────────────────────────
function Recap({ trip, activities }: { trip: Trip; activities: Activity[] }) {
  const stats = useMemo(() => computeRecapStats(trip, activities), [trip, activities]);
  const currency = trip.currency ?? "EUR";
  const showCover =
    !!trip.coverImage && !isLikelyBrokenCoverUrl(trip.coverImage);
  const status = getTripStatus(trip);

  // Interstitial after the recap has been visible long enough for the user
  // to actually take it in. Fully gated inside maybeShowRecapInterstitial:
  // no-op on web, off by default, and skipped when the daily cap is met.
  useEffect(() => {
    const t = window.setTimeout(() => {
      void maybeShowRecapInterstitial();
    }, 6000);
    return () => window.clearTimeout(t);
  }, []);

  // Celebration on first view. Warm-palette confetti burst — larger
  // than the settle-up burst (48 vs 32 pieces, 2.5 s) because the
  // recap is the once-per-trip 'thank you for using Travelix' moment.
  useEffect(() => {
    celebrate(`recap:${trip.id}`, { pieces: 48, durationMs: 2500 });
  }, [trip.id]);

  return (
    <main className="min-h-full bg-bg pb-24">
      {/* Nav — floating back button on the hero */}
      <div className="relative mx-auto max-w-2xl">
        <Link
          to={`/trip/${trip.id}`}
          aria-label="Back to trip"
          className="absolute left-3 top-3 z-10 flex h-9 w-9 items-center justify-center rounded-full text-white bg-black/40 backdrop-blur-md"
        >
          <ArrowLeft className="h-5 w-5" />
        </Link>

        {/* HERO */}
        <section className="relative aspect-[5/3] w-full overflow-hidden">
          <div
            className="absolute inset-0"
            style={{ background: "linear-gradient(135deg,#E4572E,#7A2712)" }}
            aria-hidden
          />
          {showCover ? (
            <img
              src={trip.coverImage!}
              alt=""
              className="absolute inset-0 h-full w-full object-cover"
            />
          ) : null}
          <div
            className="absolute inset-0"
            aria-hidden
            style={{
              background:
                "linear-gradient(to top, rgba(20,15,10,0.75) 0%, rgba(20,15,10,0.1) 45%, rgba(20,15,10,0.4) 100%)",
            }}
          />
          <div className="absolute inset-x-5 bottom-5 text-white">
            <p className="text-[11px] font-bold uppercase tracking-widest text-white/85">
              {status === "past" ? "A trip to remember" : "So far"}
            </p>
            <h1
              className="mt-1 font-display text-white m-0 tracking-tight leading-none"
              style={{
                fontVariationSettings: `"opsz" 96, "wght" 500`,
                fontSize: "clamp(36px, 10vw, 56px)",
                letterSpacing: "-0.02em",
              }}
            >
              {trip.name}
            </h1>
            <p className="mt-1 text-[13px] font-medium text-white/90 tracking-wide">
              {formatDateRange(trip.startDate, trip.endDate)}
              {trip.destinations.length ? ` · ${formatDestinations(trip.destinations)}` : ""}
            </p>
          </div>
        </section>
      </div>

      <div className="mx-auto max-w-2xl px-5 pt-8 flex flex-col gap-8">
        {/* In numbers */}
        <section>
          <SectionEyebrow>In numbers</SectionEyebrow>
          <div className="grid grid-cols-2 gap-3">
            <StatCard
              icon={Calendar}
              label={stats.nights === 1 ? "night" : "nights"}
              value={String(stats.nights)}
            />
            <StatCard
              icon={Users}
              label={stats.travelers === 1 ? "traveler" : "travelers"}
              value={String(stats.travelers)}
            />
            <StatCard
              icon={Sparkles}
              label={stats.activities === 1 ? "activity" : "activities"}
              value={String(stats.activities)}
            />
            <StatCard
              icon={Wallet}
              label="total spent"
              value={formatMoney(stats.totalSpent, currency)}
            />
          </div>
        </section>

        {/* Highlights */}
        {stats.highlights.length > 0 ? (
          <section>
            <SectionEyebrow>Highlights</SectionEyebrow>
            <ul className="flex flex-col divide-y divide-line rounded-xl border border-line bg-bg-elevated">
              {stats.highlights.map((h) => (
                <li key={h.label} className="flex items-center justify-between gap-3 px-4 py-3.5">
                  <span className="text-[13px] font-medium text-ink-muted">{h.label}</span>
                  <span className="text-[15px] font-semibold text-ink truncate max-w-[55%] text-right">
                    {h.value}
                  </span>
                </li>
              ))}
            </ul>
          </section>
        ) : null}

        {/* Money */}
        <section>
          <SectionEyebrow>Money</SectionEyebrow>
          <div className="rounded-xl border border-line bg-bg-elevated p-5 shadow-warm-1">
            <p className="text-[11px] font-bold uppercase tracking-widest text-ink-subtle">
              Per person
            </p>
            <p
              className="mt-1 font-display text-ink m-0 tabular"
              style={{
                fontVariationSettings: `"opsz" 60, "wght" 500`,
                fontSize: 36,
                letterSpacing: "-0.01em",
              }}
            >
              {formatMoney(stats.perPerson, currency)}
            </p>
            <div className="mt-4 flex items-center gap-2">
              <span
                className={`inline-flex items-center gap-1.5 rounded-full px-2.5 py-1 text-[11px] font-bold uppercase tracking-widest ${
                  stats.settled
                    ? "bg-success-soft text-success"
                    : "bg-warning-soft text-warning"
                }`}
              >
                {stats.settled ? "All settled up" : "Balances still open"}
              </span>
              {!stats.settled ? (
                <Button asChild variant="ghost" size="sm">
                  <Link to={`/trip/${trip.id}/expenses`}>Settle up</Link>
                </Button>
              ) : null}
            </div>
          </div>
        </section>

        {/* Ideas that never made the cut */}
        {stats.leftoverIdeas.length > 0 ? (
          <section>
            <SectionEyebrow>Try again next time</SectionEyebrow>
            <ul className="flex flex-col divide-y divide-line rounded-xl border border-line bg-bg-elevated">
              {stats.leftoverIdeas.map((idea) => (
                <li key={idea} className="flex items-center gap-3 px-4 py-3">
                  <Lightbulb className="h-4 w-4 shrink-0 text-primary" />
                  <span className="text-[14px] text-ink truncate">{idea}</span>
                </li>
              ))}
            </ul>
          </section>
        ) : null}

        {/* Footer */}
        <div className="pt-2">
          <Button asChild block size="lg">
            <Link to="/dashboard">Back to my trips</Link>
          </Button>
          <p className="mt-3 text-center text-[12px] text-ink-subtle">
            Screenshot the top to share the recap.
          </p>
        </div>
      </div>
    </main>
  );
}

// ─── Stat card ──────────────────────────────────────────────
function StatCard({
  icon: Icon,
  label,
  value,
}: {
  icon: typeof Sparkles;
  label: string;
  value: string;
}) {
  return (
    <motion.div
      initial={{ opacity: 0, y: 8 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true, margin: "-40px 0px" }}
      transition={{ duration: 0.35, ease: "easeOut" }}
      className="rounded-xl border border-line bg-bg-elevated p-4 shadow-warm-1"
    >
      <Icon className="h-4 w-4 text-primary" />
      <p
        className="mt-3 font-display text-ink m-0 tabular"
        style={{
          fontVariationSettings: `"opsz" 60, "wght" 500`,
          fontSize: 32,
          letterSpacing: "-0.01em",
          lineHeight: 1,
        }}
      >
        {value}
      </p>
      <p className="mt-1 text-[12px] uppercase tracking-widest text-ink-subtle">
        {label}
      </p>
    </motion.div>
  );
}

function SectionEyebrow({ children }: { children: React.ReactNode }) {
  return (
    <p className="mb-3 px-1 text-[11px] font-bold uppercase tracking-widest text-ink-subtle">
      {children}
    </p>
  );
}

function RecapSkeleton() {
  return (
    <main className="min-h-full bg-bg pb-24" aria-busy>
      <Skeleton className="mx-auto aspect-[5/3] w-full max-w-2xl rounded-none" />
      <div className="mx-auto max-w-2xl px-5 pt-8 flex flex-col gap-4">
        <Skeleton className="h-4 w-24" />
        <div className="grid grid-cols-2 gap-3">
          <Skeleton className="h-24 rounded-xl" />
          <Skeleton className="h-24 rounded-xl" />
          <Skeleton className="h-24 rounded-xl" />
          <Skeleton className="h-24 rounded-xl" />
        </div>
      </div>
    </main>
  );
}

// ─── Pure stats computation ─────────────────────────────────
interface RecapStats {
  nights: number;
  travelers: number;
  activities: number;
  totalSpent: number;
  perPerson: number;
  settled: boolean;
  highlights: { label: string; value: string }[];
  leftoverIdeas: string[];
}

function computeRecapStats(trip: Trip, activities: Activity[]): RecapStats {
  // allExpenses (incl. settlement payments) feeds balance/settled calc;
  // spendExpenses excludes them so a recorded payment never counts as
  // "spending" toward the total, biggest-expense, or category stats.
  const allExpenses = trip.expenses ?? [];
  const expenses = allExpenses.filter((e) => e.type !== "payment");
  const totalSpent = expenses.reduce((s, e) => s + e.amount, 0);
  const travelers = trip.memberIds?.length ?? 0;
  const perPerson = travelers > 0 ? totalSpent / travelers : 0;
  // Same definition as the Expenses tab: no direct debts left between anyone.
  const settled = computeDirectDebts(allExpenses).length === 0;
  const currency = trip.currency ?? "EUR";

  // Highlights
  const highlights: { label: string; value: string }[] = [];

  // Biggest expense
  const biggest = expenses.reduce<
    (typeof expenses)[number] | null
  >((max, e) => (max && max.amount >= e.amount ? max : e), null);
  if (biggest) {
    highlights.push({
      label: "Biggest expense",
      value: `${biggest.name} · ${formatMoney(biggest.amount, currency)}`,
    });
  }

  // Busiest day (most activities) — only if we have >1 day
  if (activities.length > 0) {
    const byDay = new Map<string, number>();
    for (const a of activities) {
      if (!a.startTime) continue;
      const k = a.startTime.toDate().toISOString().slice(0, 10);
      byDay.set(k, (byDay.get(k) ?? 0) + 1);
    }
    let busiest: { date: string; count: number } | null = null;
    for (const [date, count] of byDay) {
      if (!busiest || count > busiest.count) busiest = { date, count };
    }
    if (busiest && busiest.count > 1) {
      const d = new Date(busiest.date);
      const label = d.toLocaleDateString("en-US", {
        weekday: "long",
        month: "short",
        day: "numeric",
      });
      highlights.push({
        label: "Busiest day",
        value: `${label} · ${busiest.count} activities`,
      });
    }
  }

  // Category with most spend
  if (expenses.length > 0) {
    const byCat = new Map<string, number>();
    for (const e of expenses) byCat.set(e.type, (byCat.get(e.type) ?? 0) + e.amount);
    let top: { cat: string; amt: number } | null = null;
    for (const [cat, amt] of byCat) {
      if (!top || amt > top.amt) top = { cat, amt };
    }
    if (top) {
      const catLabel = top.cat.charAt(0).toUpperCase() + top.cat.slice(1);
      highlights.push({
        label: "Biggest category",
        value: `${catLabel} · ${formatMoney(top.amt, currency)}`,
      });
    }
  }

  // Ideas that never got scheduled
  const leftoverIdeas = (trip.ideas ?? [])
    .filter((i) => !i.scheduledActivityId)
    .slice(0, 5)
    .map((i) => i.text);

  return {
    nights: trip.durationDays ?? 0,
    travelers,
    activities: activities.length,
    totalSpent,
    perPerson,
    settled,
    highlights,
    leftoverIdeas,
  };
}
